import React, { useEffect, useMemo, useState } from "react";
import { playerCards, getProfile, handicapHistory } from "../lib/api";
import { shotsReceived, points as pointsFor } from "../lib/scoring";
import { Spinner } from "./ui";
import Scorecard, { Legend } from "./Scorecard";
import HandicapChart from "./HandicapChart";

/**
 * A player's detail, opened in place under their leaderboard row.
 *
 * Navy header, round selector on the left, Scorecard / Stats on the
 * right — the shape a tour board uses. Loads only when opened, so a
 * fifty-strong board isn't fetching fifty sets of hole scores up front.
 */

export default function PlayerPanel({ competitionId, profileId }) {
  const [who, setWho] = useState(null);
  const [cards, setCards] = useState(null);
  const [roundId, setRoundId] = useState(null);
  const [tab, setTab] = useState("card");
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);

  useEffect(() => {
    let live = true;
    Promise.all([getProfile(profileId), playerCards(competitionId, profileId),
                 handicapHistory(profileId).catch(() => [])])
      .then(([p, cs, hh]) => {
        if (!live) return;
        setWho(p);
        setHistory(hh);
        const sorted = cs.slice().sort(
          (a, b) => a.rounds.round_number - b.rounds.round_number);
        setCards(sorted);
        const played = [...sorted].reverse().find((x) => (x.hole_scores ?? []).length > 0);
        setRoundId((played ?? sorted[0])?.rounds.id ?? null);
      })
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [competitionId, profileId]);

  const worked = useMemo(() => (cards ?? []).map((c) => {
    const holes = [...(c.tee_sets?.tee_holes ?? [])]
      .sort((a, b) => a.hole_number - b.hole_number);
    const scores = {};
    (c.hole_scores ?? []).forEach((h) => { scores[h.hole_number] = h.strokes; });

    let gross = 0, pts = 0, holed = 0, notHoled = 0;
    const tally = { eagle: 0, birdie: 0, par: 0, bogey: 0, double: 0 };
    const byPar = {};

    holes.forEach((h) => {
      const v = scores[h.hole_number];
      const sh = shotsReceived(c.playing_handicap, h.stroke_index);
      pts += pointsFor(v ?? null, h.par, sh);
      if (v === null) { notHoled++; return; }
      if (v === undefined) return;
      gross += v; holed++;
      const d = v - h.par;
      if (d <= -2) tally.eagle++;
      else if (d === -1) tally.birdie++;
      else if (d === 0) tally.par++;
      else if (d === 1) tally.bogey++;
      else tally.double++;
      byPar[h.par] = byPar[h.par] || { n: 0, over: 0 };
      byPar[h.par].n++; byPar[h.par].over += d;
    });

    return {
      ...c, holes, scores, gross, pts, tally, byPar,
      entered: holed + notHoled,
      complete: holes.length > 0 && holed + notHoled === holes.length,
      nett: c.card_status === "submitted" ? gross - c.playing_handicap : null,
    };
  }), [cards]);

  const stats = useMemo(() => {
    const played = worked.filter((c) => c.complete);
    if (!played.length) return null;
    const tally = { eagle: 0, birdie: 0, par: 0, bogey: 0, double: 0 };
    const byPar = {};
    played.forEach((c) => {
      Object.keys(tally).forEach((k) => { tally[k] += c.tally[k]; });
      Object.entries(c.byPar).forEach(([p, v]) => {
        byPar[p] = byPar[p] || { n: 0, over: 0 };
        byPar[p].n += v.n; byPar[p].over += v.over;
      });
    });
    const netts = played.map((c) => c.nett).filter((n) => n != null);
    const pts = played.reduce((a, c) => a + c.pts, 0);
    return {
      rounds: played.length, tally, byPar,
      holes: Object.values(tally).reduce((a, b) => a + b, 0),
      bestNett: netts.length ? Math.min(...netts) : null,
      avgNett: netts.length ? (netts.reduce((a, b) => a + b, 0) / netts.length).toFixed(1) : null,
      totalPoints: pts,
      avgPoints: (pts / played.length).toFixed(1),
    };
  }, [worked]);

  if (error) return <div className="pp"><div className="pp-body"><div className="notice notice-error">{error}</div></div></div>;
  if (!cards || !who) {
    return (
      <div className="pp">
        <div className="pp-body" style={{ display: "grid", placeItems: "center", padding: "2rem" }}>
          <Spinner />
        </div>
      </div>
    );
  }

  const active = worked.find((c) => c.rounds.id === roundId);

  return (
    <div className="pp">
      <div className="pp-body">
        <div className="pp-bar">
          <div className="pp-pick">
            <select className="select pp-round" value={roundId ?? ""}
                    onChange={(e) => setRoundId(e.target.value)}>
              {worked.map((c) => (
                <option key={c.rounds.id} value={c.rounds.id}>
                  {c.rounds.name} · {c.rounds.courses?.name}
                </option>
              ))}
            </select>
            <span className="pp-context">
              Index {who.handicap_index ?? "—"}
              {active && ` · ${active.tee_sets?.name} tees · playing off ${active.playing_handicap}`}
            </span>
          </div>
          <div className="tabs pp-tabs">
            <button data-on={tab === "card"} onClick={() => setTab("card")}>Scorecard</button>
            <button data-on={tab === "stats"} onClick={() => setTab("stats")}>Stats</button>
          </div>
        </div>

        {tab === "card" ? (
          !active || active.entered === 0 ? (
            <div className="empty"><p>No card entered for this round yet.</p></div>
          ) : (
            <>
              <Scorecard holes={active.holes} scores={active.scores}
                         playingHandicap={active.playing_handicap} />
              <Legend />
              <div className="pp-totals">
                <div><span className="eyebrow">Gross</span><b>{active.gross}</b></div>
                <div><span className="eyebrow">Nett</span>
                  <b style={{ color: active.card_status === "no_return" ? "var(--red)" : undefined }}>
                    {active.card_status === "no_return" ? "NR" : active.nett ?? "–"}
                  </b></div>
                <div><span className="eyebrow">Points</span><b>{active.pts}</b></div>
              </div>
            </>
          )
        ) : stats ? (
          <>
            <Stats stats={stats} />
            <div style={{ marginTop: "1.25rem" }}>
              <h4 className="eyebrow" style={{ marginBottom: ".5rem" }}>Handicap index</h4>
              <HandicapChart history={history} />
            </div>
          </>
        ) : (
          <div className="empty"><p>No completed rounds yet.</p></div>
        )}
      </div>
    </div>
  );
}

function Stats({ stats }) {
  const pct = (n) => (stats.holes ? Math.round((n / stats.holes) * 100) : 0);
  const sign = (v) => (v > 0 ? `+${v.toFixed(2)}` : v.toFixed(2));

  return (
    <div className="pp-stats">
      <div>
        <h4 className="eyebrow">
          Scoring · {stats.holes} holes over {stats.rounds} round{stats.rounds === 1 ? "" : "s"}
        </h4>
        <div className="list" style={{ "--cols": "minmax(0,1fr) 3rem 3.2rem" }}>
          {[
            ["Eagles or better", stats.tally.eagle],
            ["Birdies", stats.tally.birdie],
            ["Pars", stats.tally.par],
            ["Bogeys", stats.tally.bogey],
            ["Doubles or worse", stats.tally.double],
          ].map(([label, n]) => (
            <div className="list-row" key={label}>
              <span>{label}</span>
              <span className="fig" style={{ textAlign: "right", fontSize: "1.05rem" }}>{n}</span>
              <span className="muted" style={{ textAlign: "right", fontSize: ".8125rem" }}>{pct(n)}%</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h4 className="eyebrow">Scoring by par</h4>
        <div className="list" style={{ "--cols": "minmax(0,1fr) 3rem 4.4rem" }}>
          <div className="list-row head">
            <span>Hole</span>
            <span style={{ textAlign: "right" }}>Played</span>
            <span style={{ textAlign: "right" }}>Avg to par</span>
          </div>
          {[3, 4, 5, 6].filter((p) => stats.byPar[p]?.n).map((p) => {
            const b = stats.byPar[p];
            return (
              <div className="list-row" key={p}>
                <span>Par {p}s</span>
                <span className="muted" style={{ textAlign: "right" }}>{b.n}</span>
                <span className="fig" style={{ textAlign: "right", fontSize: "1.05rem",
                      color: b.over / b.n < 0 ? "var(--red)" : undefined }}>
                  {sign(b.over / b.n)}
                </span>
              </div>
            );
          })}
        </div>
        <p className="muted" style={{ fontSize: ".75rem", marginTop: ".5rem", lineHeight: 1.6 }}>
          Average to par by hole length is the useful one — leaking a shot a
          round on the par 3s is a different problem from leaking it on the
          par 5s, and a total hides both.
        </p>
      </div>

      <div>
        <h4 className="eyebrow">Across the competition</h4>
        <div className="list">
          {[
            ["Rounds completed", stats.rounds],
            ["Best nett", stats.bestNett ?? "–"],
            ["Average nett", stats.avgNett ?? "–"],
            ["Total points", stats.totalPoints],
            ["Average points", stats.avgPoints],
          ].map(([k, v]) => (
            <div className="list-row" key={k}>
              <span>{k}</span>
              <span className="fig" style={{ fontSize: "1.05rem" }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
